import { DownloadOutlined } from '@ant-design/icons'
import { Button, Table } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'

import CreateRetargetingListButton, { CreateRetargetingListFormValues } from './CreateRetargetingListButton.tsx'
import { calculateColumnsWidth, formatNumberWithGrouping } from './dynamicColumnsHelper.ts'
import { ReportColumn } from './loadReport.ts'
import { ReportRowObject, ReportSortState } from './useReportRunner.ts'

interface Props {
  columns: ReportColumn[]
  data: ReportRowObject[]
  loading: boolean
  onSort: (key?: string, order?: 'ascend' | 'descend') => void
  titleText: string | null
  sort: ReportSortState
  totalValues?: Record<string, string | number>
  onExportCsv: () => void
  onExportXlsx: () => void
  onCreateRetargetingList?: (values: CreateRetargetingListFormValues) => Promise<void>
  showAdvertiserSelectorForRetargetingList?: boolean
}

export const ReportResultTable: React.FC<Props> = ({
  columns,
  data,
  loading,
  onSort,
  titleText,
  sort,
  totalValues,
  onExportCsv,
  onExportXlsx,
  onCreateRetargetingList,
  showAdvertiserSelectorForRetargetingList,
}) => {
  const source = useMemo(() => data, [data])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(source.length / pageSize))
    if (currentPage > maxPage) {
      setCurrentPage(maxPage)
    }
  }, [source.length, pageSize, currentPage])
  const normalizedTotals = useMemo(() => {
    if (!totalValues) return undefined
    const entries = Object.entries(totalValues).map(([key, value]) => [key.toLowerCase(), value] as const)
    return Object.fromEntries(entries) as Record<string, string | number>
  }, [totalValues])

  const asNumeric = (value: string | number | undefined) => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
    if (typeof value !== 'string') return undefined
    const normalized = value.replace(/,/g, '').trim()
    if (!normalized) return undefined
    const parsed = Number(normalized)
    return Number.isFinite(parsed) ? parsed : undefined
  }

  const renderSummary = () => {
    if (!resolvedColumns.length || !normalizedTotals) {
      return null
    }

    return (
      <Table.Summary>
        <Table.Summary.Row>
          {resolvedColumns.map((col: any, idx: number) => {
            const key = col?.dataIndex as string | undefined
            const value = key ? normalizedTotals[key.toLowerCase()] : undefined
            const numeric = asNumeric(value)
            const content =
              idx === 0 && value == null
                ? 'Total'
                : typeof numeric === 'number'
                  ? formatNumberWithGrouping(numeric)
                  : (value ?? '')
            return (
              <Table.Summary.Cell key={col.key ?? idx} index={idx}>
                <strong>{content}</strong>
              </Table.Summary.Cell>
            )
          })}
        </Table.Summary.Row>
      </Table.Summary>
    )
  }

  const { columns: resolvedColumns, tableWidth } = useMemo(() => {
    const mapped = columns.map((col) => ({
      title: col.title,
      dataIndex: col.key,
      key: col.key,
      sorter: true,
      sortOrder: sort.key === col.key ? (sort.order ?? null) : null,
      // Keep two-state sorting cycle only: ascend <-> descend (no "off" state)
      sortDirections: ['ascend', 'descend', 'ascend'] as const,
      render: (value: any) =>
        typeof value === 'number' && Number.isFinite(value) ? formatNumberWithGrouping(value) : value,
    }))

    return calculateColumnsWidth(mapped as any, source, 2000)
  }, [columns, source, sort.key, sort.order])

  return (
    <div className="reportListCard">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div className="report-title">Report on {titleText}</div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'end' }}>
          {onCreateRetargetingList && (
            <CreateRetargetingListButton
              onSave={onCreateRetargetingList}
              disabled={loading}
              showAdvertiserSelector={showAdvertiserSelectorForRetargetingList}
            />
          )}
          <Button
            type="primary"
            ghost
            style={{ width: 80, fontSize: 12 }}
            onClick={onExportCsv}
            aria-label="Download CSV"
            disabled={loading}
            loading={loading}
          >
            <DownloadOutlined /> CSV
          </Button>
          <Button
            type="primary"
            ghost
            style={{ width: 80, fontSize: 12 }}
            onClick={onExportXlsx}
            aria-label="Download XLSX"
            disabled={loading}
            loading={loading}
          >
            <DownloadOutlined /> XLSX
          </Button>
        </div>
      </div>
      <Table
        loading={loading}
        showSorterTooltip={false}
        className="report-table"
        pagination={{
          current: currentPage,
          total: source.length,
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['25', '50', '100', '200'],
        }}
        scroll={{ x: tableWidth }}
        columns={resolvedColumns as any}
        dataSource={source}
        rowKey={(row) => row.__rowId}
        onChange={(pagination: any, __, sorter: any, extra: any) => {
          if (extra?.action === 'paginate') {
            if (pagination?.current) {
              setCurrentPage(pagination.current)
            }
            if (pagination?.pageSize) {
              setPageSize(pagination.pageSize)
            }
            return
          }
          if (extra?.action !== 'sort') {
            return
          }
          if (Array.isArray(sorter)) return
          const nextOrder =
            sorter?.order ??
            (sort.key === sorter?.columnKey ? (sort.order === 'ascend' ? 'descend' : 'ascend') : 'ascend')
          onSort(sorter?.columnKey, nextOrder)
        }}
        summary={renderSummary}
      />

      {!source.length ? null : (
        <div style={{ paddingTop: 12, textAlign: 'right', color: '#888' }}>{`Rows: ${source.length}`}</div>
      )}
    </div>
  )
}
